// 用户设置对话框(右上角用户菜单 → 设置)
// 对齐 docs/ui_dialog 规范:标题栏右上角 ✕ 关闭;底部只放"保存修改"主按钮;
// modalType=alert 点遮罩不关闭。
// 本地账号(auth_provider 为空)提供修改密码标准流程;OAuth 账号引导去身份方修改。
import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  Input,
  Label,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { KeyRegular } from '@fluentui/react-icons';
import { authApi } from '@/api/auth';
import { explainError } from '@/api/client';
import { useAuthStore } from '@/store/auth';
import { DialogCloseTitle } from '@/components/DialogCloseTitle';
import { HelpPopover } from '@/components/HelpPopover';
import { toast } from '@/lib/toast';

const useStyles = makeStyles({
  field: {
    display: 'block',
    marginBottom: '16px',
  },
  input: {
    width: '100%',
  },
  note: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    lineHeight: '20px',
  },
});

export function UserSettingsDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const styles = useStyles();
  const user = useAuthStore((s) => s.user);

  const [oldPwd, setOldPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);

  const isLocal = !user?.auth_provider;
  const canSave =
    !saving && oldPwd.length > 0 && newPwd.length >= 8 && newPwd === confirm;

  function handleDismiss() {
    setOldPwd('');
    setNewPwd('');
    setConfirm('');
    onClose();
  }

  async function onSave() {
    if (newPwd !== confirm) {
      toast.warning('两次输入的新密码不一致');
      return;
    }
    setSaving(true);
    try {
      await authApi.changePassword({ old_password: oldPwd, new_password: newPwd });
      toast.success('密码已修改，下次登录请使用新密码');
      handleDismiss();
    } catch (e) {
      toast.error(explainError(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(_, d) => !d.open && handleDismiss()}
      modalType="alert"
    >
      <DialogSurface style={{ width: 460, maxWidth: '92vw' }}>
        <DialogBody>
          <DialogCloseTitle
            title="用户设置"
            onClose={handleDismiss}
            icon={<KeyRegular />}
          />
          <DialogContent>
            {!isLocal ? (
              <span className={styles.note}>
                当前账号由 OAuth 身份提供方登录，请在
                {user?.auth_provider ?? '身份提供方'}处修改密码。
              </span>
            ) : (
              <>
                <label className={styles.field}>
                  <Label required>旧密码</Label>
                  <Input
                    className={styles.input}
                    type="password"
                    autoComplete="current-password"
                    placeholder="输入当前密码"
                    value={oldPwd}
                    onChange={(_, d) => setOldPwd(d.value)}
                  />
                </label>
                <label className={styles.field}>
                  <Label required>
                    新密码 <HelpPopover content="新的登录密码，至少 8 位。" />
                  </Label>
                  <Input
                    className={styles.input}
                    type="password"
                    autoComplete="new-password"
                    placeholder="输入新密码（至少 8 位）"
                    value={newPwd}
                    onChange={(_, d) => setNewPwd(d.value)}
                  />
                </label>
                <label className={styles.field}>
                  <Label required>确认新密码</Label>
                  <Input
                    className={styles.input}
                    type="password"
                    autoComplete="new-password"
                    placeholder="再次输入新密码"
                    value={confirm}
                    onChange={(_, d) => setConfirm(d.value)}
                  />
                </label>
              </>
            )}
          </DialogContent>
          {isLocal && (
            <DialogActions>
              <Button appearance="primary" disabled={!canSave} onClick={onSave}>
                {saving ? '保存中…' : '保存修改'}
              </Button>
            </DialogActions>
          )}
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}